/**
 * Mock Orgs API Client
 *
 * Provides a configurable mock implementation for testing and development of
 * the staff-admin org picker. Returns fixtures instead of making HTTP requests.
 *
 * @module api/OrgsApiClient.mock
 */

import type { Org, ListOrgsResponse } from '../types';
import type { OrgsApiClient } from './OrgsApiClient';
import { HttpError } from './http';

// =============================================================================
// Fixture Data
// =============================================================================

/**
 * Default fixture orgs for the picker. A small, deterministic set so the picker
 * is populated in mock/dev/E2E without a live backend.
 */
export const DEFAULT_ORGS_FIXTURE: Org[] = [
  { id: 'org-456', name: 'Acme Corp', primaryDomain: 'acme.com' },
  { id: 'org-789', name: 'Globex', primaryDomain: 'globex.com' },
  { id: 'org-101', name: 'Initech', primaryDomain: 'initech.com' },
];

// =============================================================================
// Configuration Types
// =============================================================================

/**
 * Error configuration for the mock listOrgs endpoint.
 */
export type MockOrgsErrorConfig =
  | { type: 'none' }
  | { type: '401' }
  | { type: '403' }
  | { type: 'custom'; status: number; message: string };

/**
 * Configuration options for MockOrgsApiClient.
 */
export interface MockOrgsApiClientConfig {
  /**
   * Orgs fixture to return from listOrgs().
   * @default DEFAULT_ORGS_FIXTURE
   */
  orgs?: Org[];

  /**
   * Error configuration for listOrgs().
   * @default { type: 'none' }
   */
  orgsError?: MockOrgsErrorConfig;

  /**
   * Optional delay in milliseconds for all responses (useful for dev mode).
   * @default 0
   */
  delayMs?: number;

  /**
   * Whether to emit a console warning on first use.
   * @default true
   */
  emitWarning?: boolean;
}

// =============================================================================
// Mock Implementation
// =============================================================================

/**
 * Mock implementation of OrgsApiClient for testing and development.
 *
 * @example
 * ```typescript
 * const mockClient = new MockOrgsApiClient();
 * const { orgs } = await mockClient.listOrgs();
 *
 * // Simulate a non-staff user (403)
 * const forbidden = new MockOrgsApiClient({ orgsError: { type: '403' } });
 * ```
 */
export class MockOrgsApiClient implements OrgsApiClient {
  private orgs: Org[];
  private orgsError: MockOrgsErrorConfig;
  private delayMs: number;
  private emitWarning: boolean;
  private hasWarnedFirstUse = false;

  /**
   * Create a new MockOrgsApiClient.
   *
   * @param config - Optional configuration for fixtures and errors
   */
  constructor(config: MockOrgsApiClientConfig = {}) {
    // Deep clone to prevent external mutation of the shared fixture.
    this.orgs = JSON.parse(
      JSON.stringify(config.orgs ?? DEFAULT_ORGS_FIXTURE),
    );
    this.orgsError = config.orgsError ?? { type: 'none' };
    this.delayMs = config.delayMs ?? 0;
    this.emitWarning = config.emitWarning ?? true;
  }

  // ===========================================================================
  // Configuration Methods
  // ===========================================================================

  /**
   * Update the orgs fixture.
   */
  setOrgs(orgs: Org[]): void {
    this.orgs = JSON.parse(JSON.stringify(orgs));
  }

  /**
   * Configure listOrgs() to throw an error.
   */
  setOrgsError(error: MockOrgsErrorConfig): void {
    this.orgsError = error;
  }

  /**
   * Set the response delay in milliseconds.
   */
  setDelay(ms: number): void {
    this.delayMs = ms;
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  private async applyDelay(): Promise<void> {
    if (this.delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.delayMs));
    }
  }

  private warnFirstUse(): void {
    if (this.emitWarning && !this.hasWarnedFirstUse) {
      console.warn(
        '[MockOrgsApiClient] Using mock implementation. ' +
          'The org picker is populated from fixtures, not a live backend.',
      );
      this.hasWarnedFirstUse = true;
    }
  }

  private throwIfConfigured(): void {
    switch (this.orgsError.type) {
      case '401':
        throw new HttpError(401, 'Unauthorized', { error: 'unauthorized' });
      case '403':
        throw new HttpError(403, 'Forbidden', {
          code: 'NOT_STAFF',
          message: 'This user is not a platform staff admin.',
        });
      case 'custom':
        throw new HttpError(this.orgsError.status, 'Error', {
          error: this.orgsError.message,
        });
      case 'none':
      default:
        break;
    }
  }

  // ===========================================================================
  // OrgsApiClient Implementation
  // ===========================================================================

  /**
   * Returns the configured orgs fixture or throws the configured error.
   */
  async listOrgs(): Promise<ListOrgsResponse> {
    this.warnFirstUse();
    await this.applyDelay();
    this.throwIfConfigured();

    return { orgs: this.orgs.map((org) => ({ ...org })) };
  }
}

// =============================================================================
// Convenience Factory Functions
// =============================================================================

/**
 * Create a MockOrgsApiClient with the default fixture orgs.
 */
export function createMockOrgsClient(
  overrides?: Partial<MockOrgsApiClientConfig>,
): MockOrgsApiClient {
  return new MockOrgsApiClient(overrides);
}

/**
 * Create a MockOrgsApiClient with an empty org list.
 */
export function createEmptyMockOrgsClient(
  overrides?: Partial<MockOrgsApiClientConfig>,
): MockOrgsApiClient {
  return new MockOrgsApiClient({ orgs: [], ...overrides });
}
